import type {ReactNode} from 'react';
import React from 'react';
import {AiChatWindow} from '@Components/ai-chat-window/ai-chat-window';
import {type ChatMessage, ChatMessagePartType, ChatMode, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {ContextualMenu} from '@Components/contextual-menu';
import type {InEditorChatPanelProps} from '@Components/poster-editor/components/poster-editing-side-panel/components/ineditor-chat-panel/ineditor-chat-panel.types';
import {imageUrlToDataUrl} from '@Utils/image.util';
import {getUrlForGeneratedAiChatModelAssetsImage, uploadModelAsset} from '@Libraries/ai-chat.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {v4 as uuidv4} from 'uuid';
import {onReplaceMedia} from '@PosterWhiteboard/page/replace-item.class';
import {ElementDataType} from '@Libraries/add-media-library';
import {UserImageSource} from '@Libraries/user-image-library';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';

export function IneditorChatPanel(props: InEditorChatPanelProps): ReactNode {
  const id = 'ai-chat-edit-image-graphic-item';
  const {state, actions} = useAiChat();

  const handleChatIdChange = (newChatId: string | undefined): void => {
    props.onChatIdChange(newChatId);
  };

  const onComplete = async (messages: Record<string, ChatMessage>): Promise<void> => {
    const messageArray = Object.values(messages);

    const lastMessage = messageArray[messageArray.length - 1];

    if (lastMessage?.role === MessageRole.ASSISTANT) {
      const imageParts = (lastMessage.parts ?? []).filter((part) => part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE);
      if (imageParts.length > 0) {
        const dataUrl = await imageUrlToDataUrl(getUrlForGeneratedAiChatModelAssetsImage(imageParts[0].hashedId, MediaItemSizeType.HIGH_RES));
        const tempUploadingImageUID = uuidv4();
        void uploadModelAsset(imageParts[0].hashedId, tempUploadingImageUID);
        onReplaceMedia({
          type: ElementDataType.IMAGE,
          source: UserImageSource.AI_CHAT_MODEL_ASSET,
          uid: tempUploadingImageUID,
          dataUrl: dataUrl,
        });
      }
    }
  };

  return (
    <ContextualMenu contentClassName={'spacing-p-l-0 spacing-p-r-0 spacing-p-b-0 spacing-p-t-0 spacing-m-0'} id="in-editor-chat-panel" includeLeftRightPadding={false}>
      <AiChatWindow
        hasFloatingPromptBox={state.hasMessages}
        credits={state.credits}
        refreshCredits={actions.refreshCredits}
        onLoginSuccessActions={actions.onLoginSuccessActions}
        isUserLoggedIn={state.isUserLoggedIn}
        currentlyOpenedChatId={props.chatId}
        modeSpecificProps={{
          mode: ChatMode.EDIT_IMAGE_GRAPHIC_ITEM,
          graphicItemUid: props.graphicItemUid,
          posterHashedId: props.posterHashedId,
        }}
        onChatComplete={onComplete}
        id={id}
        setCurrentChatId={handleChatIdChange}
        userHasChatHistory={state.chatHistory.length > 0}
      />
    </ContextualMenu>
  );
}
